import { SPECIAL_LENSES } from '../config/appearance';

/**
 * A110T — « Voir le paysage » : colour of the world seen through a Sunset lens.
 *
 * The tints are those of the maisonmata.com article « Sunset Lenses »
 * (/blogs/news/sunset-lenses, picker « Scene 01, Bali, golden hour »): a
 * colour layer in multiply mode over the photo, top to bottom. Same values
 * here, so the live camera matches the article. « Foncé » is the article;
 * « Clair » keeps 55 % of the tint. Visual preview, not a lens measurement.
 */
export type RGB = readonly [number, number, number];

/** One stop of the tint layer: position 0 (top) … 1 (bottom), sRGB colour 0–255, opacity. */
export interface TintStop { at: number; rgb: RGB; a: number }

export const LENS_VIEW_TINTS: Record<string, readonly TintStop[]> = {
  sunset: [{ at: 0, rgb: [64, 56, 170], a: 0.62 }, { at: 0.5, rgb: [170, 90, 150], a: 0.42 }, { at: 1, rgb: [255, 128, 48], a: 0.62 }],
  sunrise: [{ at: 0, rgb: [110, 170, 235], a: 0.5 }, { at: 1, rgb: [255, 215, 50], a: 0.5 }],
  dawn: [{ at: 0, rgb: [90, 110, 170], a: 0.55 }, { at: 1, rgb: [255, 130, 110], a: 0.5 }],
  stellar: [{ at: 0, rgb: [30, 120, 255], a: 0.6 }, { at: 1, rgb: [235, 40, 90], a: 0.6 }],
  aurora: [{ at: 0, rgb: [150, 90, 220], a: 0.55 }, { at: 1, rgb: [150, 200, 255], a: 0.42 }],
  bloom: [{ at: 0, rgb: [150, 140, 145], a: 0.45 }, { at: 1, rgb: [240, 80, 140], a: 0.5 }],
  magma: [{ at: 0, rgb: [240, 50, 40], a: 0.45 }],
  coral: [{ at: 0, rgb: [140, 30, 60], a: 0.5 }, { at: 1, rgb: [216, 160, 220], a: 0.45 }],
  sun: [{ at: 0, rgb: [255, 210, 40], a: 0.45 }],
  sky: [{ at: 0, rgb: [46, 112, 232], a: 0.5 }],
  lagoon: [{ at: 0, rgb: [40, 170, 190], a: 0.48 }],
};

/** Light 30 % / Dark 85 %, as in the try-on Colours view. */
export const LENS_VIEW_DENSITIES = [30, 85] as const;
export const DEFAULT_LENS_VIEW_DENSITY = 85;
/** Share of the article tint kept by « Clair ». */
export const LIGHT_TINT_SHARE = 0.55;

export interface LensFilter {
  id: string;
  /** Multiply factors (sRGB, 0–1) at the top, the middle and the bottom of the view. */
  stops: readonly [RGB, RGB, RGB];
  /** Tint layer as CSS (the same gradient as the article, scaled for « Clair »). */
  css: string;
}

/** Snaps any try-on density to the two lens-view intensities (Light 30 / Dark 85). */
export function lensViewDensity(density: number): number {
  return density === 30 ? 30 : DEFAULT_LENS_VIEW_DENSITY;
}

export function isSunsetLens(id: string): boolean {
  return SPECIAL_LENSES.some(l => l.id === id) && id in LENS_VIEW_TINTS;
}

/**
 * Premultiplied colour and opacity of the tint layer at height y, interpolated
 * like a CSS gradient (CSS interpolates in premultiplied sRGB).
 */
function layerAt(stops: readonly TintStop[], y: number, share: number): { p: RGB; a: number } {
  const pm = (s: TintStop) => ({ p: s.rgb.map(c => (c / 255) * s.a * share) as unknown as RGB, a: s.a * share });
  if (stops.length === 1 || y <= stops[0]!.at) return pm(stops[0]!);
  const last = stops[stops.length - 1]!;
  if (y >= last.at) return pm(last);
  let i = 0;
  while (i < stops.length - 2 && y > stops[i + 1]!.at) i++;
  const s0 = pm(stops[i]!), s1 = pm(stops[i + 1]!);
  const t = (y - stops[i]!.at) / (stops[i + 1]!.at - stops[i]!.at);
  return { p: [0, 1, 2].map(k => s0.p[k]! + (s1.p[k]! - s0.p[k]!) * t) as unknown as RGB, a: s0.a + (s1.a - s0.a) * t };
}

/** Multiply-blend factor of the tint layer at height y: image × (1 − a + a·colour). */
export function multiplyFactor(stops: readonly TintStop[], y: number, share = 1): RGB {
  const { p, a } = layerAt(stops, y, share);
  return [1 - a + p[0], 1 - a + p[1], 1 - a + p[2]];
}

export function lensFilter(id: string, density: number): LensFilter {
  const stops = LENS_VIEW_TINTS[id];
  if (!stops || !SPECIAL_LENSES.some(l => l.id === id)) throw new Error('Unknown Sunset lens');
  if (!Number.isFinite(density) || density < 0 || density > 100) throw new Error('Invalid lens density');
  const share = lensViewDensity(density) === 30 ? LIGHT_TINT_SHARE : 1;
  const css = stops.length === 1
    ? `linear-gradient(${rgba(stops[0]!, share)},${rgba(stops[0]!, share)})`
    : `linear-gradient(180deg,${stops.map(s => `${rgba(s, share)} ${Math.round(s.at * 100)}%`).join(',')})`;
  return { id, stops: [multiplyFactor(stops, 0, share), multiplyFactor(stops, 0.5, share), multiplyFactor(stops, 1, share)], css };
}

function rgba(s: TintStop, share: number): string {
  return `rgba(${s.rgb[0]},${s.rgb[1]},${s.rgb[2]},${+(s.a * share).toFixed(3)})`;
}

/**
 * Cover crop of a video into a view: the part of the texture shown (scale) and
 * where it starts (offset), in 0..1 texture coordinates.
 */
export function coverCrop(videoW: number, videoH: number, viewW: number, viewH: number): { scale: [number, number]; offset: [number, number] } {
  if (videoW <= 0 || videoH <= 0 || viewW <= 0 || viewH <= 0) return { scale: [1, 1], offset: [0, 0] };
  const video = videoW / videoH;
  const view = viewW / viewH;
  if (video > view) {
    const s = view / video;
    return { scale: [s, 1], offset: [(1 - s) / 2, 0] };
  }
  const s = video / view;
  return { scale: [1, s], offset: [0, (1 - s) / 2] };
}
